import { WwwDirectusService } from 'www-cms';
import { catchError, combineLatest, map, of, tap } from 'rxjs';
import { Route, Router } from '@angular/router';
import { PageComponent } from './page/page.component';
import { appRoutes } from './app.routes';
import { pageResolver } from './page/page.resolver';

// Snapshot the original shell children once at module load so repeated
// calls to generateDynamicRoutes() (e.g. per-request SSR bootstraps) don't
// accumulate duplicate dynamic routes into the shared appRoutes array.
const SHELL_ROUTE_INDEX = appRoutes.findIndex((route) => route.path === '');
const ORIGINAL_SHELL_CHILDREN: Route[] = [
  ...(appRoutes[SHELL_ROUTE_INDEX]?.children ?? []),
];

/**
 * Dynamically generate routes based on the pages in Directus
 * @returns void
 */
export function generateDynamicRoutes(
  directus: WwwDirectusService,
  router: Router,
  isServer: boolean
) {
  return () =>
    combineLatest([directus.getPageUrls()]).pipe(
      map(([info]) => {
        const dynamicRoutes: Route[] = info.map((page) => ({
          // remove the leading slash if present
          path: page.url.replace(/^\//, ''),
          component: PageComponent,
          resolve: { page: pageResolver },
          runGuardsAndResolvers: 'paramsOrQueryParamsChange',
          // Pass the page id to be used by the resolver to fetch full data
          data: {
            id: page.id,
            revalidate: 60 * 15,
          },
        }));

        if (isServer) {
          console.log(
            `[dynamic-routes] Generated ${dynamicRoutes.length} routes from getPageUrls()`
          );
        }

        const nextRoutes = appRoutes.map((route, index) =>
          index === SHELL_ROUTE_INDEX
            ? {
                ...route,
                children: [...dynamicRoutes, ...ORIGINAL_SHELL_CHILDREN],
              }
            : route
        );
        router.resetConfig(nextRoutes);
      }),
      // Router's automatic initial navigation is disabled in app.config.ts
      // (see `withDisabledInitialNavigation()`) so it cannot race with
      // `resetConfig` above. We trigger it here once the CMS-driven routes
      // are registered.
      tap(() => {
        if (isServer) {
          console.log(
            '[dynamic-routes] Triggering initial navigation with updated config'
          );
        }
        router.initialNavigation();
      }),
      // Safety net: if `getPageUrls()` fails, we still need to start
      // navigation so the app does not hang on a blank screen. Navigation
      // will proceed against the original static `appRoutes`, meaning CMS
      // pages will fall through to the `**` wildcard, but at least the
      // static shell + not-found renders.
      catchError((err) => {
        if (isServer) {
          console.error(
            '[dynamic-routes] Failed to generate dynamic routes; falling back to static routes:',
            err
          );
        }
        router.initialNavigation();
        return of(undefined);
      })
    );
}
